import { Directive, ElementRef, HostBinding, Input, OnChanges, Renderer2, inject } from '@angular/core';
import { PorIcon } from '@por/icon-lib/icons';
import { PorIconService } from './icon.service';
import { SafeHtmlPipe } from './safe-html.pipe';

@Directive({
    selector: '[porIcon]',
    standalone: true
})
export class PorIconDirective implements OnChanges {
    protected readonly elementRef = inject(ElementRef);
    protected readonly renderer = inject(Renderer2);
    protected readonly iconService = inject(PorIconService);

    @Input() width = 16;
    @Input() height = 16;
    @Input() color = 'currentColor';
    @Input() icon!: PorIcon;
    @HostBinding('class') classes = 'flex justify-center items-center';

    ngOnChanges() {
        // all changes to input bindings ultimately require a re-render of the SVG element for display, so perform that action
        this.renderIcon();
    }

    /**
     * Render icon by retrieving the SVG element from the registry
     */
    private renderIcon() {
        const iconSVG = this.iconService.getIconSvg(this.icon);
        if (iconSVG) {
            this.setSVGElement(iconSVG);
        } else {
            this.clearSVGElement();
        }
    }

    /**
     * Set the required style related properties to the SVG element and append this to the host element.
     */
    private setSVGElement(svg: SVGElement): void {
        this.clearSVGElement();
        this.iconService.configureSVGElement(svg, this.width, this.height, this.color);
        this.renderer.appendChild(this.elementRef.nativeElement, svg);
    }

    /**
     * Remove any child SVG elements from the host element.
     */
    private clearSVGElement(): void {
        const el: HTMLElement = this.elementRef.nativeElement;
        const children = el.childNodes;
        const length = children.length;
        for (let i = length - 1; i >= 0; i--) {
            const child = children[i] as HTMLElement;
            if (child.tagName?.toLowerCase() === 'svg') {
                this.renderer.removeChild(el, child);
            }
        }
    }
}
